export const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000';

/**
 * A relative API base works in a browser — the page's own origin resolves it, and in development
 * Vite proxies it. Inside the native app there is no server behind that origin: the shell is
 * served from files in the APK, so "/api/auth/me" resolves to a bundled asset and the app gets
 * index.html where it expected JSON.
 *
 * That is not a hypothetical. The first build shipped with the development value baked in and the
 * sign-in screen reported a JSON parser error to the worker. The build now bakes an absolute
 * origin, and this is the thing that says so out loud if it ever does not.
 */
export function apiBaseLooksWrongForNative(): boolean {
  return !/^https?:\/\//i.test(API_URL) || /\/\/localhost|\/\/127\./i.test(API_URL);
}

import { t } from '../i18n/index.js';
import { isNativeApp, nativeSaveFile } from './native.js';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/**
 * A fetch that never reached the server rejects with a TypeError whose message the browser
 * writes, not us: "Failed to fetch" on Chrome, "Load failed" on Safari. It is the difference
 * between "we could not ask" and "the answer was no", and the whole offline behaviour turns on
 * telling those two apart — an ApiError means the server replied.
 */
export function isNetworkFailure(err: unknown): boolean {
  if (err instanceof ApiError) return false;
  const e = err as { name?: string; message?: string };
  return (
    e?.name === 'TypeError' ||
    /failed to fetch|load failed|networkerror|network request failed/i.test(e?.message ?? '')
  );
}

/**
 * "We did not get an answer" — which is not the same as "the answer was no".
 *
 * A dropped connection is the obvious case, but a proxy restarting in front of a healthy API
 * answers 502 or 503, and that is just as much a non-answer: nothing about the worker's session
 * has changed. Treating it as a rejection is how a worker gets shown a sign-in page in the middle
 * of a deploy, which is the same wrong screen a dropped connection used to produce.
 *
 * 401 is deliberately not here. That one really is the server saying the session is over.
 */
export function isUnreachable(err: unknown): boolean {
  if (isNetworkFailure(err)) return true;
  return err instanceof ApiError && (err.status === 0 || err.status >= 500);
}

/**
 * Read a response we expect to be JSON.
 *
 * Anything can end up in front of an API: a proxy error page, a captive portal, a login wall on
 * hotel wifi, or — as happened here — the app's own index.html. Letting JSON.parse throw sends
 * the browser's words straight to a worker: "Unexpected token '<', "<!doctype "... is not valid
 * JSON" is not a sentence anyone should read while trying to clock in.
 */
async function readJson(res: Response): Promise<Record<string, unknown>> {
  const type = res.headers.get('content-type') ?? '';
  const body = await res.text();
  if (!/\bjson\b/i.test(type) || body.trim() === '') {
    throw new ApiError(
      res.status,
      'bad_response',
      t('api_bad_response'),
      // Enough to diagnose from an audit log, never enough to put on screen.
      { content_type: type || '(none)', body_start: body.slice(0, 120) },
    );
  }
  try {
    return JSON.parse(body) as Record<string, unknown>;
  } catch {
    throw new ApiError(res.status, 'bad_response', t('api_bad_response'), {
      content_type: type,
      body_start: body.slice(0, 120),
    });
  }
}

/** JSON fetch with cookies. Throws ApiError on non-2xx. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: {
      // FormData sets its own multipart boundary; forcing JSON here corrupts the upload.
      ...(init.body !== undefined && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new CustomEvent('siva:unauthorized'));
    let body: Record<string, unknown> = {};
    try {
      body = await readJson(res);
    } catch {
      // An error response that is not JSON tells us nothing beyond its status, and the status
      // already has a message. Do not let the parse failure replace it.
    }
    const { code, message, ...details } = body;
    throw new ApiError(
      res.status,
      (code as string) ?? 'error',
      (message as string) ?? res.statusText,
      details,
    );
  }
  return res.status === 204 ? (undefined as T) : ((await readJson(res)) as T);
}

export const post = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
export const patch = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
export const put = <T>(path: string, body: unknown) =>
  api<T>(path, { method: 'PUT', body: JSON.stringify(body) });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/**
 * An iPhone or iPad, however it is being used. Safari there identifies an iPad as a Mac, so the
 * touch points are what give it away.
 */
export function isAppleMobile(): boolean {
  const n = navigator as Navigator & { platform?: string; maxTouchPoints?: number };
  return (
    /iPhone|iPad|iPod/i.test(n.userAgent) ||
    (n.platform === 'MacIntel' && (n.maxTouchPoints ?? 0) > 1)
  );
}

/**
 * Fetch a file with cookies and hand it to the browser as a download.
 *
 * On an iPhone a download link does not download. In Safari it opens a preview at best; in an
 * app added to the home screen — which is how the owner uses the admin — a click on a download
 * link does nothing at all, silently. The owner tapped Download on every report and got nothing.
 * What an iPhone does have is the share sheet, which takes a file and offers Save to Files,
 * AirDrop, Mail and the rest, and works the same from the home screen. So the file goes there.
 * Cancelling the sheet is not an error.
 *
 * The Android app's WebView has no downloads either — the same silence, and workers tapped
 * Download PDF on their slips and got nothing — so there the bytes go to the phone, which saves
 * them to Downloads/SIVA and posts a "Downloaded" notification that opens the file. Everywhere
 * else the download link is what people expect.
 *
 * Resolves with where the file went when the phone can say (the app), and null otherwise.
 */
export async function download(path: string, filename: string): Promise<{ saved: string | null }> {
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include' });
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new CustomEvent('siva:unauthorized'));
    let body: Record<string, unknown> = {};
    try {
      body = (await res.json()) as Record<string, unknown>;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(
      res.status,
      (body.code as string) ?? 'error',
      (body.message as string) ?? res.statusText,
    );
  }
  const blob = await res.blob();
  const mime = res.headers.get('content-type')?.split(';')[0] || blob.type;
  if (isNativeApp()) {
    return { saved: await nativeSaveFile(filename, mime, blob) };
  }
  // Safari in a tab has a download manager (iOS 13+), and a download link works there. It is
  // only the home-screen app that has none, and there the share sheet — Save to Files — is the
  // one door out; the owner asked twice, and there is no third.
  const standalone =
    (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (isAppleMobile() && standalone && typeof navigator.share === 'function') {
    const file = new File([blob], filename, { type: mime });
    if (navigator.canShare?.({ files: [file] })) {
      try {
        await navigator.share({ files: [file], title: filename });
      } catch (err) {
        if ((err as { name?: string })?.name === 'AbortError') return { saved: null }; // they closed the sheet
        throw err;
      }
      return { saved: null };
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return { saved: null };
}
